/**
 * AddonsSection — lista los add-ons disponibles para el plan actual y permite
 * activarlos/desactivarlos.
 *
 * Contrato backend (autoritativo):
 *   GET  /addons?planId={planId}            -> { data: AddOn[] }
 *   POST /subscriptions/{subId}/addons      -> { addOnId, action: 'activate'|'deactivate' }
 *
 * AddOn = { addOnId, name, description, pricing: { USD: { monthly } },
 *           availableFrom, featureFlags, limitsModifier, active }
 *
 * El estado "activo" se deriva de subscription.limits (featureFlags ⊆ limits).
 * Una feature incluida nativamente por el plan (no comprada como add-on) se
 * muestra como "Incluido en tu plan" y no se puede togglear desde acá.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAddons, toggleAddon } from '../services/api';
import { formatPriceWithCurrency } from '../utils/formatCurrency';
import './AddonsSection.css';

const ADDON_ICONS = {
  'afip-invoicing': '🧾',
  delivery: '🚚',
  'customer-app': '📱',
  'extra-restaurant': '🏪',
  'advanced-inventory': '📦',
};

const PLAN_ORDER = { free: 1, starter: 2, professional: 3, business: 4, enterprise: 5 };

// Plan que incluye nativamente cada feature flag (mantener sync con backend
// services/.../middleware/featureGate.js FEATURE_REQUIRED_PLAN). Si el plan
// actual es >= a este nivel, la feature viene incluida y no es un add-on.
const NATIVE_PLAN_BY_FLAG = {
  deliveryEnabled: 'business',
  customerAppEnabled: 'business',
  recipesEnabled: 'business',
  afipInvoicingEnabled: 'professional',
};

const planLevel = (planId) => PLAN_ORDER[planId] || 0;

function AddonsSection({
  subscriptionId,
  currentPlanId,
  currency = 'ARS',
  subscriptionLimits,
  variant = 'dark',
  onAddonChange,
}) {
  const { t } = useTranslation();
  const [addons, setAddons] = useState([]);
  const [limits, setLimits] = useState(subscriptionLimits || {});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [error, setError] = useState('');

  // Keep local limits in sync if the parent reloads the subscription.
  useEffect(() => {
    setLimits(subscriptionLimits || {});
  }, [subscriptionLimits]);

  const loadAddons = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getAddons(currentPlanId).catch(() => ({ data: [] }));
      setAddons(result.data || []);
    } catch {
      setAddons([]);
    } finally {
      setLoading(false);
    }
  }, [currentPlanId]);

  useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  // ¿La feature del add-on viene incluida nativamente por el plan actual?
  const isIncludedNatively = (addon) => {
    const flags = Object.keys(addon.featureFlags || {});
    return flags.some((flag) => {
      const native = NATIVE_PLAN_BY_FLAG[flag];
      return native && planLevel(currentPlanId) >= planLevel(native);
    });
  };

  // ¿El add-on está activo (comprado)? Todas sus featureFlags están en limits.
  const isAddonActive = (addon) => {
    const flags = Object.keys(addon.featureFlags || {});
    if (flags.length === 0) return false; // ej: extra-restaurant (solo limitsModifier)
    return flags.every((flag) => limits?.[flag] === true);
  };

  const handleToggle = async (addon) => {
    if (!subscriptionId || toggling) return;
    setError('');
    const activate = !isAddonActive(addon);
    try {
      setToggling(addon.addOnId);
      const result = await toggleAddon(subscriptionId, addon.addOnId, activate);
      // El backend devuelve los limits actualizados — usarlos como fuente de verdad.
      if (result?.subscription?.limits) {
        setLimits(result.subscription.limits);
      }
      if (onAddonChange) onAddonChange();
    } catch (err) {
      console.error('Error toggling addon:', err);
      setError(err.response?.data?.message || t('planSelection.addons.toggleError'));
    } finally {
      setToggling(null);
    }
  };

  if (loading) return null;
  if (addons.length === 0) return null;

  // Una "feature de tier" tiene featureFlags gateadas por plan (PWA, Delivery,
  // Inventario, AFIP). NO son add-ons vendibles en v2: vienen incluidas al
  // subir de plan (decisión Diego NER-2445). Un add-on real (ej. extra-restaurant)
  // no tiene flags gateadas por tier — sólo modifica límites — y sí es toggleable.
  const isTierFeature = (addon) =>
    Object.keys(addon.featureFlags || {}).some((flag) => NATIVE_PLAN_BY_FLAG[flag]);

  // Plan mínimo que incluye la feature (el mayor tier entre sus flags).
  const requiredPlanFor = (addon) =>
    Object.keys(addon.featureFlags || {}).reduce((best, flag) => {
      const native = NATIVE_PLAN_BY_FLAG[flag];
      return native && planLevel(native) > planLevel(best) ? native : best;
    }, null);

  const realAddons = addons.filter((addon) => !isTierFeature(addon));
  const tierFeatures = addons.filter(isTierFeature);
  const includedFeatures = tierFeatures.filter(
    (addon) => planLevel(currentPlanId) >= planLevel(requiredPlanFor(addon))
  );
  const upgradeFeatures = tierFeatures.filter(
    (addon) => planLevel(currentPlanId) < planLevel(requiredPlanFor(addon))
  );

  if (realAddons.length === 0 && tierFeatures.length === 0) return null;

  const renderFeatureCard = (addon, kind) => {
    const requiredPlan = requiredPlanFor(addon);
    return (
      <div key={addon.addOnId} className={`addon-card addon-feature addon-feature--${kind}`}>
        <div className="addon-header">
          <span className="addon-icon">{ADDON_ICONS[addon.addOnId] || '🔧'}</span>
          <div className="addon-info">
            <h3 className="addon-name">
              {t(`planSelection.addons.items.${addon.addOnId}.name`, addon.name)}
            </h3>
            <p className="addon-description">
              {t(`planSelection.addons.items.${addon.addOnId}.description`, addon.description)}
            </p>
          </div>
        </div>
        <div className="addon-footer">
          {kind === 'included' ? (
            <span className="addon-included-label">
              ✅ {t('planSelection.addons.includedBadge')}
            </span>
          ) : (
            <span className="addon-upgrade-label">
              🔓{' '}
              {t('planSelection.addons.availableInPlan', {
                plan: t(`planSelection.plans.${requiredPlan}.name`, requiredPlan),
              })}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`addons-section addons-section--${variant}`}>
      {error && <div className="addons-error">{error}</div>}

      {/* Add-ons reales (vendibles, toggleables) — sólo extra-restaurant en v2 */}
      {realAddons.length > 0 && (
        <>
          <h2 className="addons-title">{t('planSelection.addons.title')}</h2>
          <p className="addons-subtitle">{t('planSelection.addons.subtitle')}</p>
          <div className="addons-grid">
            {realAddons.map((addon) => {
              const included = isIncludedNatively(addon);
              const isActive = !included && isAddonActive(addon);
              const pricing = addon.pricing?.[currency] || addon.pricing?.ARS || {};
              const isBusy = toggling === addon.addOnId;

              return (
                <div
                  key={addon.addOnId}
                  className={`addon-card ${isActive ? 'addon-active' : ''} ${
                    included ? 'addon-included' : ''
                  }`}
                >
                  <div className="addon-header">
                    <span className="addon-icon">{ADDON_ICONS[addon.addOnId] || '🔧'}</span>
                    <div className="addon-info">
                      <h3 className="addon-name">
                        {t(`planSelection.addons.items.${addon.addOnId}.name`, addon.name)}
                      </h3>
                      <p className="addon-description">
                        {t(
                          `planSelection.addons.items.${addon.addOnId}.description`,
                          addon.description
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="addon-footer">
                    {included ? (
                      <span className="addon-included-label">
                        {t('planSelection.addons.includedInPlan')}
                      </span>
                    ) : (
                      <>
                        <span className="addon-price">
                          {pricing.monthly != null
                            ? `${formatPriceWithCurrency(pricing.monthly, currency)}/${t('planSelection.month')}`
                            : t('planSelection.contactUs')}
                        </span>
                        <button
                          type="button"
                          className={`btn ${isActive ? 'btn-secondary' : 'btn-primary'} btn-sm`}
                          onClick={() => handleToggle(addon)}
                          disabled={isBusy || !subscriptionId}
                        >
                          {isBusy
                            ? t('planSelection.processing')
                            : isActive
                              ? t('planSelection.addons.deactivate')
                              : t('planSelection.addons.activate')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Features incluidas en el plan actual (no toggleables) */}
      {includedFeatures.length > 0 && (
        <>
          <h2 className="addons-title">{t('planSelection.addons.planFeaturesTitle')}</h2>
          <p className="addons-subtitle">{t('planSelection.addons.planFeaturesSubtitle')}</p>
          <div className="addons-grid">
            {includedFeatures.map((addon) => renderFeatureCard(addon, 'included'))}
          </div>
        </>
      )}

      {/* Features que se desbloquean al subir de plan */}
      {upgradeFeatures.length > 0 && (
        <>
          <h2 className="addons-title">{t('planSelection.addons.upgradeTitle')}</h2>
          <p className="addons-subtitle">{t('planSelection.addons.upgradeSubtitle')}</p>
          <div className="addons-grid">
            {upgradeFeatures.map((addon) => renderFeatureCard(addon, 'upgrade'))}
          </div>
        </>
      )}
    </div>
  );
}

export default AddonsSection;
